import { Fragment, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Eye, Pencil, Plus, RotateCcw, Search, Trash2 } from 'lucide-react';
import type { ContainerDump } from '@container-system/shared';
import * as containerDumpsApi from '../../api/containerDumps';
import { useAuth } from '../../app/AuthContext';
import { useMasterData } from '../../hooks/useMasterData';
import { Button } from '../../components/ui/Button';
import { Card, CardBody } from '../../components/ui/Card';
import { Combobox } from '../../components/ui/Combobox';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Pagination } from '../../components/ui/DataTable';
import { Input } from '../../components/ui/Input';
import { EmptyState } from '../../components/ui/EmptyState';
import { FullPageSpinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { PageHeader } from '../../components/ui/PageHeader';
import { useToast } from '../../components/ui/Toast';
import { ApiRequestError } from '../../api/client';
import { formatDateOnly, formatDateTime } from '../../utils/format';

const PAGE_SIZE = 25;

/** Fasst aufeinanderfolgende Kippungen desselben Kalendertags zusammen - gleiche Konvention wie bei den Wiegungen. */
function groupByDay(items: ContainerDump[]): { dateLabel: string; items: ContainerDump[] }[] {
  const groups: { dateLabel: string; items: ContainerDump[] }[] = [];
  for (const item of items) {
    const label = formatDateOnly(item.dumpDate);
    const current = groups.at(-1);
    if (current && current.dateLabel === label) current.items.push(item);
    else groups.push({ dateLabel: label, items: [item] });
  }
  return groups;
}

export function ContainerDumpsListPage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('ADMIN', 'OPERATOR');
  const isAdmin = hasRole('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const { containers, materials, alloys } = useMasterData();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<ContainerDump | null>(null);
  const [detailTarget, setDetailTarget] = useState<ContainerDump | null>(null);
  const [formTarget, setFormTarget] = useState<ContainerDump | 'new' | null>(null);

  const query = useQuery({
    queryKey: ['container-dumps', 'list', page, search],
    queryFn: () => containerDumpsApi.listContainerDumps({ page, pageSize: PAGE_SIZE, search: search || undefined }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => containerDumpsApi.deleteContainerDump(id),
    onSuccess: () => {
      toast.show('Kippung gelöscht.', 'success');
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['container-dumps', 'list'] });
    },
    onError: () => toast.show('Löschen fehlgeschlagen.', 'error'),
  });

  function toggleDay(dateLabel: string) {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateLabel)) next.delete(dateLabel);
      else next.add(dateLabel);
      return next;
    });
  }

  const groups = groupByDay(query.data?.items ?? []);
  const columnCount = 6 + (canWrite ? 1 : 0);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Container-Kippung"
        description={`${query.data?.total ?? 0} Einträge gesamt`}
        actions={
          canWrite && (
            <Button size="sm" onClick={() => setFormTarget('new')}>
              <Plus className="h-4 w-4" strokeWidth={2} /> Neue Kippung
            </Button>
          )
        }
      />

      <Card>
        <CardBody className="flex flex-col gap-4">
          <Input
            placeholder="Suche nach Container, Material, Legierung…"
            icon={Search}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          {query.isLoading ? (
            <FullPageSpinner />
          ) : groups.length === 0 ? (
            <EmptyState title="Keine Kippungen gefunden." />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[13.5px]">
                  <thead>
                    <tr className="bg-accent-500/15 text-xs font-semibold uppercase text-accent-300 max-md:text-[11px]">
                      <th className="w-8 px-2 py-2.5" />
                      <th className="whitespace-nowrap px-2.5 py-2.5">Uhrzeit</th>
                      <th className="whitespace-nowrap px-2.5 py-2.5">Container 1</th>
                      <th className="whitespace-nowrap px-2.5 py-2.5">Container 2</th>
                      <th className="whitespace-nowrap px-2.5 py-2.5">Material</th>
                      <th className="whitespace-nowrap px-2.5 py-2.5">Legierung</th>
                      <th className="whitespace-nowrap px-2.5 py-2.5 text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((group) => {
                      const isCollapsed = collapsedDays.has(group.dateLabel);
                      return (
                        <Fragment key={group.dateLabel}>
                          <tr>
                            <td colSpan={columnCount} className="bg-accent-600 p-0 text-xs font-semibold uppercase tracking-wide text-white">
                              <button
                                type="button"
                                onClick={() => toggleDay(group.dateLabel)}
                                className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left hover:bg-white/10"
                              >
                                <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} strokeWidth={2} />
                                {group.dateLabel} <span className="text-white/80">· {group.items.length} Einträge</span>
                              </button>
                            </td>
                          </tr>
                          {!isCollapsed &&
                            group.items.map((d) => (
                              <tr key={d.id} className="border-b border-surface-700 hover:bg-accent-500/5">
                                <td className="px-2 py-2.5">
                                  <button
                                    title="Details anzeigen"
                                    aria-label="Details anzeigen"
                                    className="rounded-md p-1 text-ivory-500 hover:bg-surface-700 hover:text-accent-300"
                                    onClick={() => setDetailTarget(d)}
                                  >
                                    <Eye className="h-4 w-4" strokeWidth={1.75} />
                                  </button>
                                </td>
                                <td className="whitespace-nowrap px-2.5 py-2.5 font-mono text-ivory-300">{d.dumpTime}</td>
                                <td className="whitespace-nowrap px-2.5 py-2.5 font-mono">{d.containerOne.name}</td>
                                <td className="whitespace-nowrap px-2.5 py-2.5 font-mono text-ivory-300">{d.containerTwo?.name ?? '—'}</td>
                                <td className="whitespace-nowrap px-2.5 py-2.5">{d.material.name}</td>
                                <td className="whitespace-nowrap px-2.5 py-2.5 text-ivory-300">{d.alloy?.name ?? '—'}</td>
                                <td className="whitespace-nowrap px-2.5 py-2.5">
                                  <div className="flex items-center justify-end gap-1">
                                    {canWrite && (
                                      <button
                                        title="Bearbeiten"
                                        aria-label="Bearbeiten"
                                        className="rounded-md p-1.5 md:p-0.5 text-accent-400 hover:bg-surface-700 hover:text-accent-300"
                                        onClick={() => setFormTarget(d)}
                                      >
                                        <Pencil className="h-4 w-4" strokeWidth={1.75} />
                                      </button>
                                    )}
                                    {isAdmin && (
                                      <button
                                        title="Löschen"
                                        aria-label="Löschen"
                                        className="rounded-md p-1.5 md:p-0.5 text-danger-400 hover:bg-surface-700 hover:text-danger-300"
                                        onClick={() => setDeleteTarget(d)}
                                      >
                                        <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} totalPages={query.data?.totalPages ?? 1} onChange={setPage} />
            </>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Kippung löschen"
        message={`Möchtest du die Kippung von "${deleteTarget?.containerOne.name}" unwiderruflich löschen?`}
        confirmLabel="Löschen"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />

      {detailTarget && <ContainerDumpDetailModal dump={detailTarget} onClose={() => setDetailTarget(null)} />}

      {formTarget && (
        <ContainerDumpFormModal
          initial={formTarget === 'new' ? null : formTarget}
          containers={containers.data ?? []}
          materials={materials.data ?? []}
          alloys={alloys.data ?? []}
          onClose={() => setFormTarget(null)}
        />
      )}
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-ivory-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-ivory-100">{children}</dd>
    </div>
  );
}

function ContainerDumpDetailModal({ dump: d, onClose }: { dump: ContainerDump; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title="Details zur Kippung" footer={<Button onClick={onClose}>Schließen</Button>}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-warning-500/15 px-2.5 py-1 text-xs font-medium text-warning-400">
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
            Gekippt: {d.containerOne.name}
            {d.containerTwo && ` / ${d.containerTwo.name}`}
          </span>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
          <DetailRow label="Datum">{formatDateOnly(d.dumpDate)}</DetailRow>
          <DetailRow label="Uhrzeit">{d.dumpTime}</DetailRow>
          <DetailRow label="Container 1">{d.containerOne.name}</DetailRow>
          <DetailRow label="Container 2">{d.containerTwo?.name ?? '—'}</DetailRow>
          <DetailRow label="Material">{d.material.name}</DetailRow>
          <DetailRow label="Legierung">{d.alloy?.name ?? '—'}</DetailRow>
          <DetailRow label="Erfasst am">{formatDateTime(d.createdAt)}</DetailRow>
        </dl>
      </div>
    </Modal>
  );
}

function ContainerDumpFormModal({
  initial,
  containers,
  materials,
  alloys,
  onClose,
}: {
  initial: ContainerDump | null;
  containers: { id: string; name: string }[];
  materials: { id: string; name: string }[];
  alloys: { id: string; name: string }[];
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const todayKey = new Date().toISOString().slice(0, 10);

  const [containerOneId, setContainerOneId] = useState<string | null>(initial?.containerOneId ?? null);
  const [containerTwoId, setContainerTwoId] = useState<string | null>(initial?.containerTwoId ?? null);
  const [materialId, setMaterialId] = useState<string | null>(initial?.materialId ?? null);
  const [alloyId, setAlloyId] = useState<string | null>(initial?.alloyId ?? null);
  const [dumpDate, setDumpDate] = useState(initial?.dumpDate ?? todayKey);
  const [dumpTime, setDumpTime] = useState(initial?.dumpTime ?? '08:00');

  const containerOptions = containers.map((c) => ({ id: c.id, label: c.name }));
  const materialOptions = materials.map((m) => ({ id: m.id, label: m.name }));
  const alloyOptions = alloys.map((a) => ({ id: a.id, label: a.name }));

  const canSubmit = !!containerOneId && !!materialId && !!dumpDate && !!dumpTime;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        containerOneId: containerOneId!,
        containerTwoId: containerTwoId || null,
        materialId: materialId!,
        alloyId: alloyId || null,
        dumpDate,
        dumpTime,
      };
      return initial ? containerDumpsApi.updateContainerDump(initial.id, payload) : containerDumpsApi.createContainerDump(payload);
    },
    onSuccess: () => {
      toast.show('Kippung gespeichert.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['container-dumps'] });
      onClose();
    },
    onError: (err) => toast.show(err instanceof ApiRequestError ? err.message : 'Speichern fehlgeschlagen.', 'error'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={initial ? 'Kippung bearbeiten' : 'Neue Kippung'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} disabled={!canSubmit}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <Combobox label="Container 1" options={containerOptions} value={containerOneId} onChange={setContainerOneId} placeholder="Bitte wählen…" />
          <Combobox label="Container 2 (optional)" options={containerOptions} value={containerTwoId} onChange={setContainerTwoId} placeholder="Optional" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Combobox label="Material" options={materialOptions} value={materialId} onChange={setMaterialId} placeholder="Bitte wählen…" />
          <Combobox label="Legierung (optional)" options={alloyOptions} value={alloyId} onChange={setAlloyId} placeholder="Optional" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Datum" type="date" value={dumpDate} onChange={(e) => setDumpDate(e.target.value)} required />
          <Input label="Uhrzeit" type="time" value={dumpTime} onChange={(e) => setDumpTime(e.target.value)} required />
        </div>
      </div>
    </Modal>
  );
}
